import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueStepper } from '@candu-ai/craft/ui';
import { getCssResetStyleMessage, useCss } from 'craft/hooks';
import { CssSelectorId } from 'craft/types';

export const BeaconSizeStepper = ({ nodeIds }: { nodeIds: string[] }) => {
  const { css: svgCss, setCss: setSvgCss, stylesHierarchy } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.HotspotBeaconSVG,
  });

  const { fallbackValue, fallbackStyle } = stylesHierarchy?.width ?? {
    fallbackValue: undefined,
    fallbackStyle: undefined,
  };

  const width = svgCss.width ? parseFloat(svgCss.width) : undefined;
  const defaultValue = fallbackValue ? parseFloat(fallbackValue as string) : undefined;
  const displayValue = width || defaultValue;

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<number | undefined>(displayValue);
  const [inEditMode, setInEditMode] = useState(false);

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        setSvgCss({ width: undefined });
      } else if (newValue >= 10 && newValue <= 80) {
        setSvgCss({ width: `${newValue}px` });
      }
    }
  };

  useEffect(() => {
    // when value changes due to external factors (caller auto-adjusted value, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (displayValue !== inputValue) {
        setInputValue(displayValue);
      }
    }
  }, [displayValue, inEditMode]);

  return (
    <ValueBox label="Size">
      <ValueStepper
        ref={inputRef}
        step={1}
        name="beacon-size"
        placeholder={!inEditMode ? 'unset' : undefined}
        valueSuffix="px"
        editable
        format="inline"
        minValue={10}
        maxValue={80}
        onChange={onSetValue}
        onBlur={() => {
          setInEditMode(false);
        }}
        value={inputValue}
        onResetTooltip={fallbackStyle && getCssResetStyleMessage(fallbackStyle)}
        onReset={
          typeof inputValue !== 'undefined' &&
          typeof fallbackValue !== 'undefined' &&
          inputValue !== defaultValue
            ? () => {
                setSvgCss({ width: undefined });
                setInputValue(defaultValue);
              }
            : undefined
        }
      />
    </ValueBox>
  );
};
